<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps<{
  modelValue: string;
  icon?: string;
  placeholder?: string;
  disabled?: boolean;
  showDelete?: boolean;
}>();

const emit = defineEmits<{
  (e: 'save', text: string): void;
  (e: 'update:modelValue', text: string): void;
  (e: 'cancel'): void;
  (e: 'delete'): void;
  (e: 'startEdit'): void;
  (e: 'finishEdit'): void;
}>();

const isEditing = ref(false);
const localValue = ref(props.modelValue);
const isHoveringEdit = ref(false);

watch(
  () => props.modelValue,
  (val) => {
    if (!isEditing.value) localValue.value = val;
  },
);

function startEdit() {
  if (!props.disabled) {
    isHoveringEdit.value = false;
    isEditing.value = true;
    localValue.value = props.modelValue;
    emit('startEdit');
  }
}

function save() {
  isEditing.value = false;
  emit('update:modelValue', localValue.value.trim());
  emit('save', localValue.value.trim());
  emit('finishEdit');
}

function cancel() {
  isEditing.value = false;
  localValue.value = props.modelValue;
  emit('cancel');
  emit('finishEdit');
}
</script>

<template>
  <div class="full-width q-pa-xs" :class="{ 'highlight-bg': isHoveringEdit }">
    <template v-if="!isEditing">
      <div class="row items-center justify-between full-width">
        <div class="col text-left">
          <slot :value="modelValue">
            <span>{{ modelValue }}</span>
          </slot>
        </div>
        <div class="col-auto action-buttons" @mouseenter="isHoveringEdit = true" @mouseleave="isHoveringEdit = false">
          <slot name="actions" />
          <q-btn dense unelevated size="sm" :icon="icon || 'edit'" @click.stop="startEdit" v-if="!disabled" />
          <q-btn dense unelevated size="sm" icon="delete" @click.stop="emit('delete')" v-if="showDelete" />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="column full-width">
        <q-input
          v-model="localValue"
          outlined
          type="textarea"
          autofocus
          :placeholder="placeholder"
          @keyup.enter="save"
          @keyup.esc="cancel"
          class="q-mr-xs full-width"
        />
        <div class="row justify-end q-gutter-sm q-mt-xs">
          <q-btn dense rounded size="sm" color="positive" icon="done" @click.stop="save" />
          <q-btn dense rounded size="sm" color="negative" icon="close" @click.stop="cancel" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.highlight-bg {
  background-color: rgba(0, 125, 186, 0.12);
  transition: background 0.2s;
}

.action-buttons {
  display: flex;
  align-items: center;
  margin-left: 4px;
}
</style>
